// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * A small modal form over Kortlægning (Tilføj ressource, Tilføj kolonne),
 * and Overblik's Sagsdata editor (wider, through `className`).
 * Keys follow `formKeyDown`: Esc anywhere cancels (nothing is saved yet),
 * Ctrl/⌘+Enter submits; plain Enter submits natively. Tab is trapped inside
 * the dialog (`trapTab`, as in EditDialog). The page returns focus to the
 * table when the dialog closes.
 *
 * Its stylesheet also carries the field and button classes a form inside it
 * uses (`field`, `fieldLabel`, `input`, `btnGhost`, `btnPrimary`), composed
 * from the shared controls — a form imports those from here.
 */

import { useId, useRef, type ReactNode } from 'react';

import { formKeyDown } from '../../app/editorKeys';
import { trapTab } from '../../app/focusTrap';
import styles from './FormDialog.module.css';

export interface FormDialogProps {
  title: string;
  /** A line under the title (muted); it also describes the dialog. */
  subtitle?: ReactNode;
  onCancel: () => void;
  onSubmit: () => void;
  children: ReactNode;
  /** The footer buttons; the primary one is `type="submit"`. */
  actions: ReactNode;
  error?: string | null;
  /** Added to the dialog box, e.g. a wider form's width. */
  className?: string;
  /** The form validates its fields itself (no native validation bubbles). */
  noValidate?: boolean;
  /**
   * Render the same form as a panel in the page (Rediger › Import,
   * frontend-u0o.1): no scrim, not modal, no focus trap. Esc still cancels.
   */
  inline?: boolean;
}

export function FormDialog({ title, subtitle, onCancel, onSubmit, children, actions, error, className, noValidate, inline }: FormDialogProps) {
  const titleId = useId();
  const subtitleId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const base = inline ? styles.inline : styles.dialog;
  const form = (
      <form
        ref={formRef}
        className={className ? `${base} ${className}` : base}
        role={inline ? undefined : 'dialog'}
        aria-modal={inline ? undefined : 'true'}
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        noValidate={noValidate}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        onKeyDown={(e) => {
          if (!inline) trapTab(e, formRef.current);
          formKeyDown(e, { onCancel, onSubmit });
        }}
      >
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        {subtitle && (
          <p id={subtitleId} className={styles.subtitle}>
            {subtitle}
          </p>
        )}
        <div className={styles.body}>{children}</div>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <div className={styles.actions}>{actions}</div>
      </form>
  );
  if (inline) return form;
  return (
    <div className={styles.scrim} onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      {form}
    </div>
  );
}
